import { MarketNews, EconomicEvent, TrendingCoin, MarketOverview } from '../types/marketData';

// News sources configuration
// const NEWS_SOURCES: NewsSource[] = [
//   { name: 'CoinDesk', url: 'https://coindesk.com', enabled: true, priority: 1 },
//   { name: 'CoinTelegraph', url: 'https://cointelegraph.com', enabled: true, priority: 2 },
//   { name: 'CryptoSlate', url: 'https://cryptoslate.com', enabled: true, priority: 3 },
//   { name: 'Decrypt', url: 'https://decrypt.co', enabled: true, priority: 4 },
//   { name: 'The Block', url: 'https://theblock.co', enabled: true, priority: 5 }
// ];

// Free news APIs
const NEWS_APIS = {
  COIN_GECKO_NEWS: 'https://api.coingecko.com/api/v3/coins/markets',
  COIN_GECKO_TRENDING: 'https://api.coingecko.com/api/v3/search/trending',
  COIN_GECKO_GLOBAL: 'https://api.coingecko.com/api/v3/global',
  FEAR_GREED: 'https://api.alternative.me/fng/',
  BINANCE_24HR: 'https://api.binance.com/api/v3/ticker/24hr',
  COIN_CAP: 'https://api.coincap.io/v2/assets'
};

export class NewsService {
  private newsCache: MarketNews[] = [];
  private lastFetch = 0;
  private cacheDuration = 5 * 60 * 1000; // 5 minutes

  // Fetch news from CoinGecko market data (converted to news format)
  public async fetchCryptoPanicNews(): Promise<MarketNews[]> {
    try {
      const response = await fetch(`${NEWS_APIS.COIN_GECKO_NEWS}?vs_currency=usd&order=market_cap_desc&per_page=20&page=1&sparkline=false&price_change_percentage=24h`);
      const data = await response.json();
      
      if (Array.isArray(data)) {
        return data.slice(0, 10).map((coin: any) => {
          const change24h = coin.price_change_percentage_24h || 0;
          
          const isPositive = change24h > 0;
          const newsTitle = `${coin.name} ${isPositive ? 'surges' : 'drops'} ${Math.abs(change24h).toFixed(1)}% in 24h`;
          
          return {
            id: `cg-${coin.id}`,
            title: newsTitle,
            summary: `${coin.name} (${coin.symbol.toUpperCase()}) is currently trading at $${coin.current_price?.toLocaleString() || 'N/A'} with a ${isPositive ? 'positive' : 'negative'} ${Math.abs(change24h).toFixed(1)}% change in the last 24 hours.`,
            url: `https://www.coingecko.com/en/coins/${coin.id}`,
            source: 'CoinGecko',
            publishedAt: Date.now() - Math.random() * 3600000,
            category: this.categorizeNews(coin.name),
            sentiment: isPositive ? 'positive' : 'negative',
            impact: Math.abs(change24h) > 15 ? 'high' : Math.abs(change24h) > 8 ? 'medium' : 'low',
            tags: [coin.symbol.toUpperCase()],
            imageUrl: coin.image
          };
        });
      }
    } catch (error) {
      console.warn('Failed to fetch CoinGecko market news:', error);
    }
    
    return [];
  }


  // Fetch trending coins from CoinGecko
  public async fetchTrendingCoins(): Promise<TrendingCoin[]> {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/search/trending');
      const data = await response.json();
      
      if (data.coins) {
        return data.coins.map((coin: any) => ({
          id: coin.item.id,
          symbol: coin.item.symbol.toUpperCase(),
          name: coin.item.name,
          price: coin.item.price_btc,
          change24h: 0,
          volume24h: 0,
          marketCap: 0,
          rank: coin.item.market_cap_rank || 0,
          sparkline: coin.item.data?.sparkline || []
        }));
      }
    } catch (error) {
      console.warn('Failed to fetch trending coins:', error);
    }
    
    return [];
  }


  // Fetch market overview
  public async fetchMarketOverview(): Promise<MarketOverview> {
    try {
      const response = await fetch('https://api.coingecko.com/api/v3/global');
      const data = await response.json();
      
      if (data.data) {
        return {
          totalMarketCap: data.data.total_market_cap.usd,
          totalVolume: data.data.total_volume.usd,
          bitcoinDominance: data.data.market_cap_percentage.btc,
          ethereumDominance: data.data.market_cap_percentage.eth,
          activeCryptocurrencies: data.data.active_cryptocurrencies,
          markets: data.data.markets,
          timestamp: Date.now()
        };
      }
    } catch (error) {
      console.warn('Failed to fetch market overview:', error);
    }
    
    return {
      totalMarketCap: 0,
      totalVolume: 0,
      bitcoinDominance: 0,
      ethereumDominance: 0,
      activeCryptocurrencies: 0,
      markets: 0,
      timestamp: Date.now()
    };
  }

  // Fetch fear & greed index
  public async fetchFearGreedIndex(): Promise<number> {
    try {
      const response = await fetch('https://api.alternative.me/fng/');
      const data = await response.json();
      
      if (data.data && data.data.length > 0) {
        return parseInt(data.data[0].value);
      }
    } catch (error) {
      console.warn('Failed to fetch fear & greed index:', error);
    }
    
    return 50; // Neutral fallback
  }

  // Fetch economic calendar events using free APIs
  public async fetchEconomicEvents(): Promise<EconomicEvent[]> {
    try {
      // Try multiple free sources
      const [marketWatchEvents, bloombergEvents] = await Promise.allSettled([
        this.fetchMarketWatchEconomicEvents(),
        this.fetchBloombergEconomicEvents()
      ]);

      const events: EconomicEvent[] = [];
      
      if (marketWatchEvents.status === 'fulfilled') {
        events.push(...marketWatchEvents.value);
      }
      
      if (bloombergEvents.status === 'fulfilled') {
        events.push(...bloombergEvents.value);
      }

      // Sort by time and return up to 10 events
      return events
        .sort((a, b) => a.time - b.time)
        .slice(0, 10);
    } catch (error) {
      console.warn('Failed to fetch economic events:', error);
      return [];
    }
  }

  // Fetch economic events from MarketWatch
  private async fetchMarketWatchEconomicEvents(): Promise<EconomicEvent[]> {
    try {
      // Use CORS proxy for MarketWatch
      const proxyUrl = 'https://api.allorigins.win/raw?url=';
      const response = await fetch(proxyUrl + encodeURIComponent('https://feeds.content.dowjones.io/public/rss/mw_topstories'));
      const text = await response.text();
      
      const events: EconomicEvent[] = [];
      
      // Look for economic event keywords in MarketWatch RSS
      const economicKeywords = [
        { keyword: 'federal reserve', title: 'Federal Reserve Meeting', importance: 'high' },
        { keyword: 'interest rate', title: 'Interest Rate Decision', importance: 'high' },
        { keyword: 'inflation', title: 'Inflation Data', importance: 'high' },
        { keyword: 'gdp', title: 'GDP Growth Rate', importance: 'high' },
        { keyword: 'unemployment', title: 'Unemployment Rate', importance: 'medium' },
        { keyword: 'non-farm payrolls', title: 'Non-Farm Payrolls', importance: 'high' },
        { keyword: 'cpi', title: 'Consumer Price Index', importance: 'high' },
        { keyword: 'retail sales', title: 'Retail Sales', importance: 'medium' }
      ];
      
      economicKeywords.forEach((item, index) => {
        if (text.toLowerCase().includes(item.keyword)) {
          events.push({
            id: `mw-${index}-${Date.now()}`,
            title: item.title,
            country: 'United States',
            currency: 'USD',
            importance: item.importance as 'high' | 'medium' | 'low',
            actual: undefined,
            forecast: undefined,
            previous: undefined,
            time: Date.now() + (index * 3600000), // Spread events over next few hours
            impact: 'neutral'
          });
        }
      });

      return events;
    } catch (error) {
      console.warn('Failed to fetch MarketWatch events:', error);
    }
    
    return [];
  }

  // Fetch economic events from Bloomberg
  private async fetchBloombergEconomicEvents(): Promise<EconomicEvent[]> {
    try {
      // Use CORS proxy for Bloomberg
      const proxyUrl = 'https://api.allorigins.win/raw?url=';
      const response = await fetch(proxyUrl + encodeURIComponent('https://feeds.bloomberg.com/markets/news.rss'));
      const text = await response.text();
      
      const events: EconomicEvent[] = [];
      
      // Look for economic event keywords in Bloomberg RSS
      const economicKeywords = [
        { keyword: 'fed', title: 'Federal Reserve Policy', importance: 'high' },
        { keyword: 'rate decision', title: 'Central Bank Rate Decision', importance: 'high' },
        { keyword: 'economic data', title: 'Economic Data Release', importance: 'medium' },
        { keyword: 'employment', title: 'Employment Report', importance: 'high' },
        { keyword: 'manufacturing', title: 'Manufacturing Data', importance: 'medium' },
        { keyword: 'consumer', title: 'Consumer Data', importance: 'medium' }
      ];
      
      economicKeywords.forEach((item, index) => {
        if (text.toLowerCase().includes(item.keyword)) {
          events.push({
            id: `bb-${index}-${Date.now()}`,
            title: item.title,
            country: 'United States',
            currency: 'USD',
            importance: item.importance as 'high' | 'medium' | 'low',
            actual: undefined,
            forecast: undefined,
            previous: undefined,
            time: Date.now() + (index * 7200000), // Spread events over next few hours
            impact: 'neutral'
          });
        }
      });

      return events;
    } catch (error) {
      console.warn('Failed to fetch Bloomberg events:', error);
    }
    
    return [];
  }


  // Get all news (with caching)
  public async getAllNews(): Promise<MarketNews[]> {
    const now = Date.now();
    
    if (now - this.lastFetch < this.cacheDuration && this.newsCache.length > 0) {
      return this.newsCache;
    }

    try {
      const [cryptoPanicNews] = await Promise.all([
        this.fetchCryptoPanicNews()
      ]);

      this.newsCache = [...cryptoPanicNews].sort((a, b) => b.publishedAt - a.publishedAt);
      this.lastFetch = now;
      
      return this.newsCache;
    } catch (error) {
      console.warn('Failed to fetch news:', error);
      return this.newsCache;
    }
  }

  // Helper methods
  private categorizeNews(title: string): MarketNews['category'] {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('bitcoin') || titleLower.includes('btc')) return 'bitcoin';
    if (titleLower.includes('ethereum') || titleLower.includes('eth')) return 'ethereum';
    if (titleLower.includes('defi') || titleLower.includes('decentralized')) return 'defi';
    if (titleLower.includes('nft')) return 'nft';
    if (titleLower.includes('regulation') || titleLower.includes('sec') || titleLower.includes('cftc')) return 'regulation';
    if (titleLower.includes('adoption') || titleLower.includes('institutional')) return 'adoption';
    
    return 'general';
  }

  private analyzeSentiment(title: string): MarketNews['sentiment'] {
    const titleLower = title.toLowerCase();
    const positiveWords = ['bullish', 'surge', 'rally', 'gains', 'positive', 'adoption', 'breakthrough', 'milestone'];
    const negativeWords = ['bearish', 'crash', 'drop', 'decline', 'negative', 'regulation', 'ban', 'hack', 'scam'];
    
    const positiveCount = positiveWords.filter(word => titleLower.includes(word)).length;
    const negativeCount = negativeWords.filter(word => titleLower.includes(word)).length;
    
    if (positiveCount > negativeCount) return 'positive';
    if (negativeCount > positiveCount) return 'negative';
    return 'neutral';
  }

  private assessImpact(title: string, votes?: any): MarketNews['impact'] {
    const titleLower = title.toLowerCase();
    const highImpactWords = ['crash', 'surge', 'breakthrough', 'regulation', 'adoption', 'institutional'];
    const hasHighImpact = highImpactWords.some(word => titleLower.includes(word));
    
    if (hasHighImpact || (votes && votes.positive > 50)) return 'high';
    if (votes && votes.positive > 20) return 'medium';
    return 'low';
  }

  private assessEconomicImpact(event: any): EconomicEvent['impact'] {
    const importance = event.importance || 'medium';
    const actual = event.actual;
    const forecast = event.forecast;
    
    if (importance === 'high') {
      if (actual && forecast) {
        return actual > forecast ? 'positive' : 'negative';
      }
    }
    
    return 'neutral';
  }
}

export const newsService = new NewsService();
